import { type RefObject, useEffect, useRef } from "react";

const SEEN_FLUSH_DELAY_MS = 500;
const SEEN_VISIBILITY_THRESHOLD = 0.5;

export function useSeenOnScreen(
  containerRef: RefObject<HTMLElement | null>,
  unseenIds: string[],
  onSeen: (ids: string[]) => void,
  enabled = true,
) {
  const sentIdsRef = useRef<Set<string>>(new Set());
  const onSeenRef = useRef(onSeen);
  const unseenKey = unseenIds.join(",");

  useEffect(() => {
    onSeenRef.current = onSeen;
  }, [onSeen]);

  useEffect(() => {
    const container = containerRef.current;
    const pendingIds = new Set(
      unseenKey
        .split(",")
        .filter((id) => id && !sentIdsRef.current.has(id)),
    );
    if (!enabled || !container || pendingIds.size === 0) {
      return;
    }
    const visibleIds = new Set<string>();
    let flushTimer: number | undefined;
    const flush = () => {
      flushTimer = undefined;
      if (document.visibilityState !== "visible" || visibleIds.size === 0) {
        return;
      }
      const ids = Array.from(visibleIds);
      ids.forEach((id) => sentIdsRef.current.add(id));
      visibleIds.clear();
      onSeenRef.current(ids);
    };
    const scheduleFlush = () => {
      if (flushTimer === undefined) {
        flushTimer = window.setTimeout(flush, SEEN_FLUSH_DELAY_MS);
      }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-item-id");
          if (!id) {
            return;
          }
          if (entry.isIntersecting && !sentIdsRef.current.has(id)) {
            visibleIds.add(id);
          } else {
            visibleIds.delete(id);
          }
        });
        scheduleFlush();
      },
      { threshold: SEEN_VISIBILITY_THRESHOLD },
    );
    container.querySelectorAll<HTMLElement>("[data-item-id]").forEach((element) => {
      const id = element.getAttribute("data-item-id");
      if (id && pendingIds.has(id)) {
        observer.observe(element);
      }
    });
    document.addEventListener("visibilitychange", scheduleFlush);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", scheduleFlush);
      window.clearTimeout(flushTimer);
    };
  }, [containerRef, enabled, unseenKey]);
}
